import { useCallback, useMemo } from 'react';

import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { numberFormatterLocal } from '@pp/core/src/utils/formatters';

import { Bets } from '../../../config/bets';
import { getDotOrCommaIndex, hasDotOrComma } from '../../../config/utils';
import { useBetStepperSlice } from '../../../store/slices/betStepper/selectors';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';
import { EXCEPTION_CURRENCIES_FOR_DECIMAL_HIDE } from '../constants';

export const useValidBetInputValue = (
  betId: Bets
): {
  betInputValue: string;
  betInputAmount: number;
} => {
  const betLimits = useBetSlice.betLimits() ?? {};
  const betFieldObject = useBetStepperSlice.getBetInputValue();
  const isBetFinalized = useBetStepperSlice.isBetFinalized(betId);
  const remainingBalance = useHighflyerStateSlice.balance();
  const totalBet = useBetSlice.totalBet();
  const locale = useSettingsSlice.currentLanguage();
  const currency = useGameStateSlice.getCurrency() ?? '';

  const formatNumber = useMemo(
    () =>
      new Intl.NumberFormat(numberFormatterLocal(locale), {
        style: 'decimal',
        minimumFractionDigits: EXCEPTION_CURRENCIES_FOR_DECIMAL_HIDE.includes(currency) ? 0 : 2,
        maximumFractionDigits: 2,
      }),
    [locale, currency]
  );

  const convertInputValueToAmount = useCallback(
    (value: string): { betInputAmount: number; betMaxLimitReached: boolean; balanceInSufficientReached: boolean } => {
      const finalBetFieldValue = value.replace(',', '.');
      const currentBalance = remainingBalance + totalBet;
      let actualBetAmount = +finalBetFieldValue;

      //Entered input is only Dot or comma
      if (!finalBetFieldValue || finalBetFieldValue === '.') {
        actualBetAmount = betLimits[betId].min;
      }
      const isBetMaxLimitExceed = actualBetAmount >= betLimits[betId].max && currentBalance >= betLimits[betId].max;
      const isBetLessThanBetMinLimit = actualBetAmount < betLimits[betId].min;
      const isBalanceInSufficient = currentBalance <= 0 ? true : actualBetAmount >= currentBalance;

      //Entered input less than tableMin limit
      if (isBetLessThanBetMinLimit) {
        actualBetAmount = betLimits[betId].min;
      }
      //Entered input more the available balance
      if (isBalanceInSufficient) {
        actualBetAmount = currentBalance;
      }
      //Entered input more than tableMax limit
      if (isBetMaxLimitExceed) {
        //Setting available balance or table max limit
        actualBetAmount = currentBalance < betLimits[betId].max ? currentBalance : betLimits[betId].max;
      }
      const finalBetAmount = actualBetAmount < betLimits[betId].min ? betLimits[betId].min : actualBetAmount;
      const adjustedBetAmount = EXCEPTION_CURRENCIES_FOR_DECIMAL_HIDE.includes(currency)
        ? Math.round(finalBetAmount)
        : finalBetAmount;
      return {
        betInputAmount: adjustedBetAmount,
        betMaxLimitReached: isBetMaxLimitExceed,
        balanceInSufficientReached: isBalanceInSufficient,
      };
    },
    [betId, betLimits, remainingBalance, totalBet, currency]
  );

  return useMemo(() => {
    let basicValidatedString = betFieldObject[betId]?.replace(/[^0-9.,]/g, '') ?? '';
    const dotOrCommaIndex = getDotOrCommaIndex(basicValidatedString);
    const isDotOrCommaExisted = hasDotOrComma(basicValidatedString);
    const stringAfterTheDotOrComma = basicValidatedString.slice(dotOrCommaIndex + 1);
    const secondDotOrCommaIndex = getDotOrCommaIndex(stringAfterTheDotOrComma);

    if (isDotOrCommaExisted) {
      //When second dot or comma entered
      if (secondDotOrCommaIndex >= 0) {
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + secondDotOrCommaIndex + 1);
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + 3);
      }
      //Correcting to 2 decimal points
      else {
        basicValidatedString = basicValidatedString.slice(0, dotOrCommaIndex + 3);
      }
    }

    const betInputObject = convertInputValueToAmount(basicValidatedString);
    return {
      betInputValue: isBetFinalized ? formatNumber.format(betInputObject.betInputAmount) : basicValidatedString,
      betInputAmount: betInputObject.betInputAmount,
    };
  }, [betFieldObject[betId], betId, convertInputValueToAmount, formatNumber, isBetFinalized]);
};
